/** Live HR window aligned with backend hr-update heartbeat (5s tick, §6.8). */
export const HR_LIVE_SAMPLE_MAX_AGE_MS = 12000;

export const HR_HISTORY_WINDOW_MS = 60_000;

export const HR_TREND_SEGMENT_MS = 15_000;

export const MIN_SAMPLES_FOR_CHART = 5;

export function isLiveHrSample(recordedAt, nowMs = Date.now()) {
  if (recordedAt == null || recordedAt === "") return false;
  const t = new Date(recordedAt).getTime();
  if (!Number.isFinite(t)) return false;
  return nowMs - t <= HR_LIVE_SAMPLE_MAX_AGE_MS;
}

export function playerHistoryKey(playerId, userId) {
  if (playerId != null && playerId !== "") return `p:${String(playerId)}`;
  if (userId != null && userId !== "") return `u:${String(userId)}`;
  return null;
}

export function readHrValue(entry) {
  const hr = Number(entry?.hr_value ?? entry?.heart_rate);
  return Number.isFinite(hr) && hr > 0 ? hr : null;
}

/**
 * Keys whose live BPM should be cleared (`hr_value: 0` or missing recorded_at after association switch).
 */
export function extractHrClearKeysFromPayload(payload) {
  if (!payload) return [];
  const keys = [];
  const pushIfClear = (p) => {
    if (!p) return;
    const key = playerHistoryKey(p.player_id, p.user_id);
    if (!key) return;
    const hr = Number(p?.hr_value ?? p?.heart_rate);
    if (Number.isFinite(hr) && hr <= 0) keys.push(key);
  };

  if (
    (payload.player_id || payload.user_id) &&
    !Array.isArray(payload.tables)
  ) {
    pushIfClear(payload);
    return keys;
  }

  const tables = Array.isArray(payload.tables) ? payload.tables : [];
  for (const tbl of tables) {
    const players = Array.isArray(tbl?.players) ? tbl.players : [];
    for (const p of players) pushIfClear(p);
  }
  return keys;
}

/**
 * Normalize hr-update snapshots and player_heart_rate increments into sample rows.
 */
export function extractHrSamplesFromPayload(payload, nowMs = Date.now()) {
  if (!payload) return [];
  const teId = payload.te_id != null ? String(payload.te_id) : null;

  if (
    (payload.player_id || payload.user_id) &&
    !Array.isArray(payload.tables)
  ) {
    const bpm = readHrValue(payload);
    if (bpm == null || !isLiveHrSample(payload.recorded_at, nowMs)) return [];
    const key = playerHistoryKey(payload.player_id, payload.user_id);
    if (!key) return [];
    const t = new Date(payload.recorded_at).getTime();
    if (!Number.isFinite(t)) return [];
    return [{ key, bpm, t, teId }];
  }

  const samples = [];
  const tables = Array.isArray(payload.tables) ? payload.tables : [];
  for (const tbl of tables) {
    const players = Array.isArray(tbl?.players) ? tbl.players : [];
    for (const p of players) {
      const bpm = readHrValue(p);
      if (bpm == null || !isLiveHrSample(p.recorded_at, nowMs)) continue;
      const key = playerHistoryKey(p.player_id, p.user_id);
      if (!key) continue;
      const t = new Date(p.recorded_at).getTime();
      if (!Number.isFinite(t)) continue;
      samples.push({ key, bpm, t, teId });
    }
  }
  return samples;
}

export function appendToHistoryMap(
  map,
  sample,
  windowMs = HR_HISTORY_WINDOW_MS,
  nowMs = Date.now()
) {
  const { key, bpm, t } = sample;
  const prev = map[key] || [];
  const last = prev[prev.length - 1];
  if (last && last.t === t && last.bpm === bpm) return map;
  const next = [...prev, { t, bpm }];
  const cutoff = nowMs - windowMs;
  const trimmed = next.filter((s) => s.t >= cutoff);
  return { ...map, [key]: trimmed };
}

export function ingestPayloadIntoHistory(
  map,
  payload,
  windowMs = HR_HISTORY_WINDOW_MS,
  nowMs = Date.now()
) {
  const samples = extractHrSamplesFromPayload(payload, nowMs);
  let next = map;
  for (const sample of samples) {
    next = appendToHistoryMap(next, sample, windowMs, nowMs);
  }
  return next;
}

export function trimHistoryMap(
  map,
  windowMs = HR_HISTORY_WINDOW_MS,
  nowMs = Date.now()
) {
  const cutoff = nowMs - windowMs;
  const next = {};
  let changed = false;
  for (const [key, arr] of Object.entries(map)) {
    const trimmed = (arr || []).filter((s) => s.t >= cutoff);
    if (trimmed.length !== (arr || []).length) changed = true;
    if (trimmed.length) next[key] = trimmed;
  }
  return changed ? next : map;
}

export function samplesForPlayer(map, playerId, userId) {
  const key = playerHistoryKey(playerId, userId);
  if (!key) return [];
  return map[key] || [];
}

export function latestBpmFromSamples(samples) {
  if (!samples?.length) return null;
  return samples[samples.length - 1].bpm;
}

/** True when a player has live or in-progress HR samples (show coach HR UI). */
export function playerHasCollectingHr(
  samples,
  maxAgeMs = HR_LIVE_SAMPLE_MAX_AGE_MS,
  nowMs = Date.now()
) {
  if (!samples?.length) return false;
  if (samples.length < MIN_SAMPLES_FOR_CHART) return true;
  return !isSignalStale(samples, maxAgeMs, nowMs);
}

export function isSignalStale(
  samples,
  maxAgeMs = HR_LIVE_SAMPLE_MAX_AGE_MS,
  nowMs = Date.now()
) {
  if (!samples?.length) return true;
  const last = samples[samples.length - 1];
  return nowMs - last.t > maxAgeMs;
}

/**
 * Compare average BPM in the last 15s vs the prior 15s.
 */
export function computeTrend(
  samples,
  segmentMs = HR_TREND_SEGMENT_MS,
  nowMs = Date.now()
) {
  if (!samples || samples.length < 2) return "unknown";
  const recentCutoff = nowMs - segmentMs;
  const priorCutoff = nowMs - segmentMs * 2;
  const recent = samples.filter((s) => s.t >= recentCutoff);
  const prior = samples.filter(
    (s) => s.t >= priorCutoff && s.t < recentCutoff
  );
  if (!recent.length || !prior.length) return "unknown";
  const avgRecent =
    recent.reduce((sum, s) => sum + s.bpm, 0) / recent.length;
  const avgPrior = prior.reduce((sum, s) => sum + s.bpm, 0) / prior.length;
  const delta = avgRecent - avgPrior;
  if (delta >= 3) return "rising";
  if (delta <= -3) return "easing";
  return "steady";
}

export function chartDataFromSamples(
  samples,
  windowMs = HR_HISTORY_WINDOW_MS,
  nowMs = Date.now()
) {
  const cutoff = nowMs - windowMs;
  return (samples || [])
    .filter((s) => s.t >= cutoff)
    .map((s) => ({
      t: s.t,
      offsetSec: Math.round((s.t - nowMs) / 1000),
      bpm: s.bpm,
      timeLabel: `${Math.round((s.t - nowMs) / 1000)}s`,
    }));
}
